import React from 'react';
import ReactTooltip from 'react-tooltip';
import { Anchor, Accordion, Icon, Row, Col, Div, Text } from '@pinpoint/design-kit';

import { withFixedTitle, insights } from 'src/components';

import SquishlessSummary from './summary';
import SquishlessSuggestions from './suggestions';

const SquishlessAccordion = withFixedTitle(Accordion);

/* eslint-disable react/prop-types */

export default function Takeaways({ name, actionableFactors, actionableFacets, toggle, styleOverride, eventEmitter }) {
  const customerGroup =
    name.toLowerCase() === 'random_sample'
      ? 'customer group'
      : name
          .replace('_', ' ')
          .split(' ')
          .map((x) => x[0].toUpperCase() + x.slice(1).toLowerCase())
          .join(' ');
  const wordsRegexp = /<i>(.*?)<\/i>/g;
  const takeawayWords = [
    ...new Set(
      actionableFacets
        .map((facet) => insights[facet.trait.code][facet.score > 50 ? 'high' : 'low'])
        .map((insight) => [...insight.matchAll(wordsRegexp)].map((m) => m[1]))
        .flat()
    ),
  ];
  const takeawayColors = [
    ...new Set(
      actionableFactors
        .map((factor) => insights[factor.trait.code][factor.score > 50 ? 'high' : 'low'])
        .map((insight) => [...insight.matchAll(wordsRegexp)].map((m) => m[1]))
        .flat()
    ),
  ];
  let analysis = '';
  let prevQualifier = '';
  const facetsAccordion = [];
  for (let i = 0; i < actionableFacets.length; i += 1) {
    const { score, trait } = actionableFacets[i];
    let range = '';
    let join = ',';
    if (i === 0) join = '';
    if (i === actionableFacets.length - 1) join = ' and';
    if (score > 50) {
      range = trait.highRangeName;
    } else {
      range = trait.lowRangeName;
    }
    let qualifier = 'slightly';
    let titleSuffix = (
      <Icon
        name="SlightlyGoodSignal"
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 324 302"
        data-tip="slightly actionable"
      />
    );
    if (score < 10 || score > 90) {
      qualifier = 'remarkably';
      titleSuffix = (
        <Icon
          name="RemarkableSignal"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 324 302"
          data-tip="remarkably actionable"
        />
      );
    } else if (score < 25 || score > 75) {
      qualifier = 'very';
      titleSuffix = (
        <Icon
          name="VeryGoodSignal"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 324 302"
          data-tip="very actionable"
        />
      );
    } else if (score < 40 || score > 60) {
      qualifier = 'moderately';
      titleSuffix = (
        <Icon
          name="ModeratelyGoodSignal"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 324 302"
          data-tip="moderately actionable"
        />
      );
    }
    if (qualifier === prevQualifier) {
      analysis = `${analysis}${join} ${range.toLowerCase()}`;
    } else {
      analysis = `${analysis}${join} ${qualifier} ${range.toLowerCase()}`;
    }
    facetsAccordion.push({
      title: `${i + 1}. ${qualifier[0].toUpperCase()}${qualifier.slice(1).toLowerCase()} ${range}`,
      content: (
        <>
          <Text
            textColor="brand500"
            dangerouslySetInnerHTML={{
              __html: insights[trait.code][score > 50 ? 'high' : 'low'],
            }}
          />
          <Text textSize="body" m={{ y: '2rem' }}>
            <i>
              {range} is defined by a&nbsp;
              <Anchor
                onClick={() => {
                  ReactTooltip.hide();
                  toggle();
                  setTimeout(() => {
                    eventEmitter.dispatch('factorChanged', `px_dim_${trait.code[9]}`);
                    eventEmitter.dispatch('facetChanged', trait.code);
                    eventEmitter.dispatch('traitChanged', trait);
                    eventEmitter.dispatch('scoreChanged', score);
                  }, 100);
                }}
                data-tip="Take me to personality explorer"
              >
                {score > 50 ? 'high' : 'low'} {trait.trait} score
              </Anchor>
              &nbsp;in the Big Five personality model.
            </i>
          </Text>
        </>
      ),
      titleSuffix,
    });
    prevQualifier = qualifier;
  }

  return (
    <Row p={{ y: '0.5rem' }} minH="calc(100% - 80px - 1.5rem)">
      <Col size={{ xs: '12', lg: '8' }}>
        <Div minH="400px" h="100%" w="100%" border="16px solid" rounded="md" style={styleOverride}>
          <SquishlessSummary
            h="auto"
            title="Summary"
            customerGroup={customerGroup}
            analysis={analysis}
            toggle={toggle}
          />
          <SquishlessAccordion
            title="Prominent Personality Types"
            contentStyleExtra={{
              h: '200px',
              overflow: 'auto',
              textSize: 'subheader',
            }}
            containerStyle={{
              m: { x: '1rem' },
            }}
            sections={facetsAccordion}
            h="auto"
          />
        </Div>
      </Col>
      <Col size={{ xs: '12', lg: '4' }}>
        <Div d="flex" flexDir="column" h="100%" w="100%">
          <Div
            minH="200px"
            h="100%"
            w="100%"
            border="16px solid"
            rounded="md"
            style={styleOverride}
            m={{ t: { xs: '1rem', lg: '0rem' }, b: { lg: '0.5rem' } }}
          >
            <SquishlessSuggestions title="Word Recommendations" list={takeawayWords} />
          </Div>
          <Div
            minH="200px"
            h="auto"
            w="100%"
            border="16px solid"
            rounded="md"
            style={styleOverride}
            m={{ t: { xs: '1rem', lg: '0.5rem' } }}
          >
            <SquishlessSuggestions title="Color Recommendations" list={takeawayColors} />
          </Div>
        </Div>
      </Col>
    </Row>
  );
}
